/**
 * BookEntry — «Лаборатория превращений. Том I. Миндаль»
 *
 * Entry Screen with strict responsive rules:
 *   • Mobile (<lg): vertical master asset, white/milky typography over it.
 *   • Desktop (≥lg): 16:9 master asset, deep noble brown typography.
 *
 * Two separate <img> layers — never crop/stretch one into the other.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getVolumeEntry } from "@/lib/bookApi";
import { useAuth } from "@/context/AuthContext";

export default function BookEntry() {
  const { slug = "almond" } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const d = await getVolumeEntry(slug);
        if (!stop) setData(d);
      } catch (e) {
        if (!stop) setErr(e?.response?.data?.detail || "Не удалось загрузить Том.");
      } finally {
        if (!stop) setLoading(false);
      }
    })();
    return () => { stop = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#efe8dc]"
           data-testid="book-entry-loading" />
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#efe8dc] text-[#3b2b1e] px-6"
           data-testid="book-entry-error">
        <div className="text-center max-w-md">
          <div className="font-serif text-2xl mb-3">Том не найден</div>
          <div className="text-sm opacity-80">{err || "Проверьте адрес ссылки."}</div>
          <button
            className="mt-6 px-6 py-3 rounded-full bg-[#3b2b1e] text-[#efe8dc]"
            onClick={() => nav("/hall")}
            data-testid="book-entry-back-hall">
            Вернуться в холл
          </button>
        </div>
      </div>
    );
  }

  const { volume, has_access, product_slug, is_editor } = data;
  const canRead = has_access || is_editor;

  const goRead = () => {
    // Book Content Screen (progressive disclosure)
    nav(`/book/${volume.slug}`);
  };

  const goPurchase = () => {
    if (product_slug) nav(`/programs/${product_slug}`);
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#efe8dc]"
         data-testid="book-entry-screen">

      {/* ============ Mobile master (vertical) ============ */}
      <div
        className="lg:hidden absolute inset-0 z-0 bg-cover bg-center"
        style={{
          backgroundImage: `url("${volume.entry_mobile_image_url || "/almond-book/entry-mobile.jpeg"}")`,
        }}
        data-testid="book-entry-bg-mobile"
      />
      {/* subtle gradient for text legibility on mobile — white typography */}
      <div className="lg:hidden absolute inset-0 z-[1]"
           style={{
             background:
               "linear-gradient(180deg, rgba(20,10,4,0.15) 0%, rgba(20,10,4,0.05) 30%, rgba(20,10,4,0.45) 78%, rgba(20,10,4,0.65) 100%)",
           }}
      />

      {/* ============ Desktop master (16:9) ============ */}
      <div
        className="hidden lg:block absolute inset-0 z-0 bg-cover bg-center"
        style={{
          backgroundImage: `url("${volume.entry_desktop_image_url || "/almond-book/entry-desktop.png"}")`,
        }}
        data-testid="book-entry-bg-desktop"
      />

      {/* ============ Mobile content — white/milky over the vertical image ============ */}
      <div className="lg:hidden relative z-10 min-h-screen flex flex-col justify-end px-7 pb-14 pt-24 text-[#f9f2e4]">
        <div className="text-[11px] tracking-[0.42em] uppercase mb-5 opacity-90"
             data-testid="book-entry-eyebrow-mobile">
          Лаборатория превращений
        </div>
        <h1
          className="font-serif leading-[0.98] text-[44px] mb-4"
          style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif" }}
          data-testid="book-entry-title-mobile"
        >
          {volume.title}
        </h1>
        {volume.subtitle && (
          <div className="text-[15px] italic opacity-90 mb-6 font-light"
               data-testid="book-entry-subtitle-mobile">
            {volume.subtitle}
          </div>
        )}
        {volume.hero_quote && (
          <div className="text-[13.5px] leading-relaxed opacity-85 mb-10 pr-4">
            «{volume.hero_quote}»
          </div>
        )}
        <div className="flex flex-col gap-3">
          {canRead ? (
            <button
              onClick={goRead}
              data-testid="book-entry-enter-mobile"
              className="w-full py-4 rounded-full bg-[#f9f2e4] text-[#2a1a0f] tracking-[0.24em] text-[13px] uppercase font-medium">
              Войти в Том
            </button>
          ) : (
            <>
              <button
                onClick={goPurchase}
                data-testid="book-entry-purchase-mobile"
                className="w-full py-4 rounded-full bg-[#f9f2e4] text-[#2a1a0f] tracking-[0.24em] text-[13px] uppercase font-medium">
                Приобрести Том
              </button>
              {!user && (
                <button
                  onClick={() => nav("/login")}
                  data-testid="book-entry-login-mobile"
                  className="w-full py-3 rounded-full border border-[#f9f2e4]/60 text-[#f9f2e4] tracking-[0.2em] text-[12px] uppercase">
                  Войти в кабинет
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ============ Desktop content — deep noble brown over the wide image ============ */}
      <div className="hidden lg:flex relative z-10 min-h-screen items-center">
        <div
          className="pl-[8vw] pr-[4vw] max-w-[720px] py-24"
          style={{ color: "#3b241a" }}
        >
          <div className="text-[11px] tracking-[0.5em] uppercase mb-6 opacity-80"
               data-testid="book-entry-eyebrow-desktop">
            Лаборатория превращений
          </div>
          <h1
            className="font-serif leading-[0.98] mb-6"
            style={{
              fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
              fontSize: "clamp(60px, 6vw, 108px)",
              color: "#2a1810",
            }}
            data-testid="book-entry-title-desktop"
          >
            {volume.title}
          </h1>
          {volume.subtitle && (
            <div className="italic text-[22px] mb-8 opacity-80"
                 data-testid="book-entry-subtitle-desktop"
                 style={{ color: "#4a2f1e" }}>
              {volume.subtitle}
            </div>
          )}
          {volume.hero_quote && (
            <div className="text-[17px] leading-relaxed mb-14 max-w-[540px]"
                 style={{ color: "#3b241a" }}>
              «{volume.hero_quote}»
            </div>
          )}
          <div className="flex items-center gap-4">
            {canRead ? (
              <button
                onClick={goRead}
                data-testid="book-entry-enter-desktop"
                className="px-10 py-4 rounded-full tracking-[0.3em] text-[12px] uppercase"
                style={{ background: "#2a1810", color: "#efe4d0" }}>
                Войти в Том
              </button>
            ) : (
              <>
                <button
                  onClick={goPurchase}
                  data-testid="book-entry-purchase-desktop"
                  className="px-10 py-4 rounded-full tracking-[0.3em] text-[12px] uppercase"
                  style={{ background: "#2a1810", color: "#efe4d0" }}>
                  Приобрести Том
                </button>
                {!user && (
                  <button
                    onClick={() => nav("/login")}
                    data-testid="book-entry-login-desktop"
                    className="px-8 py-3.5 rounded-full tracking-[0.24em] text-[12px] uppercase border"
                    style={{ borderColor: "#3b241a", color: "#3b241a" }}>
                    Войти в кабинет
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Top-left return arrow (both breakpoints, contextual color) */}
      <button
        onClick={() => nav(user ? "/hall" : "/")}
        data-testid="book-entry-back"
        className="absolute z-20 top-5 left-5 lg:top-8 lg:left-8 w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-md
                   bg-white/15 text-[#f9f2e4] lg:bg-[#2a1810]/10 lg:text-[#2a1810]"
        aria-label="Назад"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
    </div>
  );
}
